import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { http, formatErr } from "../lib/api";
import { usePolling } from "../lib/usePolling";
import { useAuth } from "../contexts/AuthContext";
import { notifTitle, notifDetail, formatDateTimeIT } from "../lib/labels";
import { toast } from "sonner";

export function NotificationBell({ tone = "dark" }) {
  const { user, loading } = useAuth();
  const enabled = !loading && !!user;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread: 0, items: [], archived_count: 0 });
  const [showArchived, setShowArchived] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async (arch = showArchived) => {
    try {
      const { data } = await http.get("/crm/me/notifications", { params: arch ? { archived: true } : {} });
      setData(data);
    } catch { /* noop */ }
  };
  const toggleArchived = () => { const v = !showArchived; setShowArchived(v); load(v); };
  const archive = async (e, n) => { e.stopPropagation(); try { await http.post(`/crm/me/notifications/${n.id}/${showArchived ? "unarchive" : "archive"}`); await load(); } catch (err) { toast.error(formatErr(err)); } };
  const archiveAllRead = async () => { try { const { data: r } = await http.post("/crm/me/notifications/archive-read"); await load(); toast.success(`${r.archived} notifiche lette archiviate.`); } catch (err) { toast.error(formatErr(err)); } };

  useEffect(() => {
    if (enabled) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  usePolling(() => load(), { interval: 10000, enabled });

  // ricarica immediata quando una sezione marca lette le proprie novità
  useEffect(() => {
    const h = () => { if (enabled) load(); };
    window.addEventListener("glowbit:notif-refresh", h);
    return () => window.removeEventListener("glowbit:notif-refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openNotif = async (n) => {
    try {
      if (!n.read) await http.post(`/crm/me/notifications/${n.id}/read`);
      const { data: t } = await http.get(`/crm/me/notifications/${n.id}/target`);
      await load();
      if (t.available) { setOpen(false); navigate(t.path); }
      else toast.warning(t.reason);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-black/5" aria-label="Notifiche">
        <Bell className={`w-5 h-5 ${tone === "dark" ? "text-white/80" : "text-neutral-700"}`} />
        {data.unread > 0 && (
          <span data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center font-semibold">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto rounded-xl shadow-xl border bg-white z-50"
          style={{ borderColor: "var(--border-silver)" }}>
          <div className="px-4 py-3 border-b text-sm font-semibold flex items-center justify-between gap-2" style={{ borderColor: "var(--border-silver)" }}>
            <span data-testid="notif-panel-title">{showArchived ? `Archiviate (${data.archived_count})` : `Notifiche${data.unread > 0 ? ` (${data.unread} non lette)` : ""}`}</span>
            <span className="flex gap-2 text-[11px] font-normal">
              {!showArchived && data.items.some((n) => n.read) && <button onClick={archiveAllRead} data-testid="notif-archive-all-read" className="underline">Archivia lette</button>}
              <button onClick={toggleArchived} data-testid="notif-toggle-archived" className="underline">{showArchived ? "Torna alle notifiche" : `Archiviate (${data.archived_count || 0})`}</button>
            </span>
          </div>
          {data.items.length === 0 && <div className="px-4 py-6 text-sm text-neutral-500" data-testid="notif-empty">{showArchived ? "Nessuna notifica archiviata." : "Nessuna notifica."}</div>}
          {data.items.map((n) => (
            <div key={n.id} role="button" tabIndex={0} onClick={() => openNotif(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b last:border-0 text-sm hover:bg-neutral-50 cursor-pointer ${n.read ? "opacity-70" : ""}`}
              style={{ borderColor: "var(--border-silver)" }}>
              <div className="flex items-center gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-red-600 shrink-0" />}
                <span className="font-medium text-neutral-800 flex-1">{notifTitle(n.type)}</span>
                {n.read && <button onClick={(e) => archive(e, n)} data-testid={`notif-archive-${n.id}`} className="text-[11px] underline text-neutral-500">{showArchived ? "Ripristina" : "Archivia"}</button>}
              </div>
              {notifDetail(n) && <div className="text-xs text-neutral-600 mt-0.5 ml-4">{notifDetail(n)}</div>}
              <div className="text-[11px] text-neutral-400 mt-0.5">{formatDateTimeIT(n.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
